import { ROUTES } from 'config';
import { ActionsMenu } from 'melp-design/components';
import { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useConfirm } from 'store/confirm';
import { useModalContext } from 'store/modal';
import { NewItem, useCopyNew, useRemoveNew, useUpdateNew } from 'store/news';
import { usePredefinedToasts } from 'utils/Toast';
import {
  EMOJI_BY_REACTION,
  reactionsVariants,
  useCreateReaction,
  useDeleteReaction,
} from 'store/reactions';
import { NewsThreadModal } from 'containers/news';
import { generateDynamicLink, includes } from 'utils/general';
import moment from 'moment';
import { useMe } from 'state/Administrators';
import { ShareModal } from 'components/modals';
import { NewNameModal } from '../../new-details/atoms';

interface Props extends Pick<NewItem, 'id' | 'name' | 'status' | 'reactions'> {
  type?: ComponentProps<typeof ActionsMenu>['type'];
}

export const NewActions: FC<Props> = ({
  id,
  name,
  status,
  reactions,
  type,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const history = useHistory();
  const { confirmation } = useConfirm();
  const { openModal } = useModalContext();
  const { hasEditPermission } = useMe();

  const { mutate: copyNew, isLoading: isCopying } = useCopyNew();
  const { mutate: updateNew, isLoading: isUpdating } = useUpdateNew();
  const { mutate: removeNew, isLoading: isRemoving } = useRemoveNew();
  const { mutate: createReaction, isLoading: isCreatingReaction } =
    useCreateReaction();
  const { mutate: deleteReaction, isLoading: isDeletingReaction } =
    useDeleteReaction();

  const copyTitle = t('common.copy');
  const titlePrefix = `(${
    copyTitle.charAt(0).toUpperCase() + copyTitle.slice(1)
  })`;

  return (
    <ActionsMenu
      type={type}
      items={[
        {
          label: t('actions.comment'),
          disabled: !hasEditPermission(['news']),
          onClick: () => {
            openModal({
              component: NewsThreadModal,
              props: {
                newId: id,
              },
              size: 'xl',
            });
          },
        },
        {
          label: t('actions.make_copy'),
          disabled: !hasEditPermission(['news']) || isCopying,
          onClick: async () => {
            const response = await openModal({
              component: NewNameModal,
              props: {
                initialName: `${titlePrefix} ${name}`,
              },
            });

            if (response.action === 'CONFIRM') {
              copyNew(
                { newId: id, name: response.name },
                {
                  onSuccess: ({ data }) => {
                    predefinedToasts.success.added();
                    history.push(
                      `${ROUTES.news.details.replace(
                        ':id',
                        data.id,
                      )}?guided=true&tab=content`,
                    );
                  },
                },
              );
            }
          },
        },
        ...(includes(['published', 'expiresSoon'], status)
          ? [
              {
                label: t('global.actions.share'),
                disabled: !hasEditPermission(['news']),
                onClick: () => {
                  openModal({
                    component: ShareModal,
                    props: {
                      url: generateDynamicLink({
                        path: 'new',
                        params: { newId: id },
                      }),
                    },
                  });
                },
              },
              {
                label: t('common.hide'),
                disabled: !hasEditPermission(['news']) || isUpdating,
                onClick: () => {
                  updateNew(
                    {
                      newId: id,
                      update: {
                        inactiveAt: moment().toISOString(true),
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
              },
            ]
          : []),
        ...(includes(['inactive', 'expired'], status)
          ? [
              {
                label: t('common.unhide'),
                disabled: !hasEditPermission(['news']) || isUpdating,
                onClick: () => {
                  updateNew(
                    {
                      newId: id,
                      update: {
                        publishedAt: moment().toISOString(true),
                        inactiveAt: null,
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
              },
            ]
          : []),
        {
          label: t('actions.delete'),
          disabled: !hasEditPermission(['news']) || isRemoving,
          onClick: async () => {
            const { confirmed } = await confirmation(
              t('common.areYouSureToDelete', {
                title: name,
                interpolation: { escapeValue: false },
              }),
              undefined,
              'danger',
            );

            if (confirmed) {
              removeNew(
                { newId: id },
                {
                  onSuccess: () => {
                    predefinedToasts.success.deleted();
                    history.push(ROUTES.news.list);
                  },
                },
              );
            }
          },
          color: 'error',
        },
        ...(reactions.selected
          ? [
              {
                label: [
                  EMOJI_BY_REACTION[reactions.selected],
                  t(`reactions.variant.${reactions.selected}`),
                ].join(' '),
                disabled: !hasEditPermission(['news']) || isDeletingReaction,
                onClick: () => {
                  deleteReaction({
                    hostId: id,
                  });
                },
                color: 'primary',
              } as const,
            ]
          : [
              null,
              ...reactionsVariants.map((reaction) => ({
                label: [
                  EMOJI_BY_REACTION[reaction],
                  t(`reactions.variant.${reaction}`),
                ].join(' '),
                disabled: !hasEditPermission(['news']) || isCreatingReaction,
                onClick: () => {
                  createReaction({
                    entity: 'news',
                    hostId: id,
                    emoji: reaction,
                  });
                },
              })),
            ]),
      ]}
    />
  );
};
